<template>
    <v-dialog fullscreen v-model="dialog" v-stack="{ open: dialog, close: hide }" transition="dialog-bottom-transition">
        <div class="summary-screen" :class="{ 'summary-screen--mobile': isMobile }">
            <div class="summary-brand">
                <brand-backdrop />
                <div class="summary-brand-accent"></div>
                <div class="summary-brand-content">
                    <img :src="logoSidebar" class="summary-brand-logo" alt="" />
                    <div class="summary-brand-title">{{ title }}</div>
                    <div class="summary-brand-sub">{{ $t('routines.summary') }}</div>
                </div>
            </div>

            <div class="summary-content">
                <div class="summary-header">
                    <span class="summary-origin">
                        {{ isCompetence ? $t('common.competence') : $t('common.routine') }}
                    </span>
                    <button v-if="backButton" type="button" class="summary-close" @click="hide">
                        <v-icon size="18">mdi-close</v-icon>
                    </button>
                </div>
                <div class="summary-content-body">
                    <skeleton-loader v-if="loading" type="rows" :count="6" />
                    <summary-data v-else :summary="summary" @back="goToRoutines" :is-competence="isCompetence"
                        :hideBackRoutines="hideBackRoutines" :preSummary="preSummary"
                        :is-second-dialog="isSecondDialog" />
                </div>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { mapMutations } from "vuex";
import { db } from "@/utils/Firebase";
import SummaryData from "@/components/student/SummaryData";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import BrandBackdrop from "@/components/common/BrandBackdrop";
import StatusBarManager from "@/utils/StatusBarManager";
import { calculateWellnessPreInfo, getStudentChanges } from "../../utils/Routine";

export default {
    name: "SummaryAssignedRoutineDialog",
    components: { SkeletonLoader, SummaryData, BrandBackdrop },
    props: {
        assignedRoutineId: {
            type: String,
            default: '',
        },
        backButton: {
            type: Boolean,
            default: false,
        },
        isCoachWatch: {
            type: Boolean,
            default: false,
        },
        isCompetence: {
            type: Boolean,
            default: false,
        },
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
        hideBackRoutines: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            canExit: false,
            title: '',
            summary: null,
            RpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            RirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            preSummary: null,
            logoSidebar: require('@/assets/img/logo_sidebar.png'),
        }
    },
    computed: {
    },
    methods: {
        ...mapMutations(['setTitle']),
        async show() {
            const vm = this
            await StatusBarManager.set(true)
            await vm.loadSummaryData()
            vm.dialog = true
        },
        async hide() {
            const vm = this
            if (vm.canExit || vm.backButton) {
                await StatusBarManager.reset()
                vm.dialog = false
                vm.$emit('hide')
            }
        },
        async goToRoutines() {
            const vm = this
            vm.canExit = true
            vm.setTitle('')
            let routeName = 'student.routines'
            if (vm.isCoachWatch) routeName = `${vm.accountType}.calendar`
            await StatusBarManager.reset()
            vm.pushRoute({ name: routeName })
        },
        async loadSummaryData() {
            const vm = this
            if (!vm.assignedRoutineId) return;
            await vm.withLoading(async () => {
            let assignedRoutineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutineId).get()
            let assignedRoutine = assignedRoutineRef.data()
            if (!assignedRoutine) {
                return
            }

            vm.preSummary = assignedRoutine?.advance?.preSummary || null

            // CURRENT ROUTINE NAME
            if (!assignedRoutine.name && !assignedRoutine.versus) {
                let routineRef = await db.collection('routines').doc(vm.assignedRoutine.routineId).get()
                vm.title = routineRef.data().name
            } else vm.title = assignedRoutine.name || assignedRoutine.versus

            let summary = {}
            const advance = assignedRoutine.advance || {}

            // POST
            summary.hasPost = advance.post === true

            // COMMENT
            summary.comment = advance.postInfo && advance.postInfo.comment || null

            // COMPETENCE
            summary.versus = assignedRoutine.versus || null
            summary.position = advance.postInfo && advance.postInfo.position || null
            summary.team = advance.postInfo && advance.postInfo.team || null

            // CURRENT DURATION
            summary.duration = advance.postInfo && advance.postInfo.duration || 0
            summary.effort = advance.postInfo && advance.postInfo.effort || 0

            // CHARGE
            summary.charge = summary.duration * summary.effort

            // WELLNESS
            let preInfo = advance.preInfo
            summary.wellness = calculateWellnessPreInfo(preInfo)

            // CHANGES
            summary.changes = [...getStudentChanges(assignedRoutine)].map(([key, groups]) => {
                const [block, ...name] = key.split('|')
                return { block: Number(block), name: name.join('|'), groups }
            })

            // REPS PER EXERCISE
            if (!vm.isCompetence) {
                let reps = 0
                let exercises = 0
                let sets = 0;
                (assignedRoutine.blocks || []).forEach(block => {
                    if (block.isCircuit) {
                        sets += block.sets || 0
                    }
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            exercises++
                            if (block.isCircuit && exercise.config?.type === 'reps') {
                                reps += Number(exercise.config.value || 0)
                            } else {
                                exercise.config?.sets?.forEach(set => {
                                    sets++
                                    let currentReps = set?.data?.find(data => data.short_name === 'short_reps')
                                    reps += currentReps && Number(currentReps.value) || 0
                                })
                            }
                        }
                    })
                })

                summary.reps = reps
                summary.exercises = exercises
                summary.sets = sets
            }

            vm.summary = summary
            })
        },
        getValueFromTable(variable, value, set) {
            const vm = this
            if (variable === vm.$t('exercises.rpe')) {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (variable === vm.$t('exercises.rir')) {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[`${variable}Table`]?.find(table => table.name === value) || null
            let reps = set?.data?.find(data => data.short_name === 'short_reps') || null
            let repsValue = reps && Number(reps.value || 0) - 1 || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && reps && findValueTable.reps[repsValue] || 0
        }
    },
    watch: {
        assignedRoutineId() {
            this.loadSummaryData()
        }
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        if (vm.canExit) return next()
        return next(false)
    }
}
</script>

<style scoped>
.summary-screen {
    display: flex;
    flex-direction: row;
    height: 100%;
    background: var(--v-surface-base);
}

.summary-brand {
    position: relative;
    flex: 0 0 46%;
    overflow: hidden;
    background: #06080C;
}

.summary-brand-accent {
    position: absolute;
    top: 10%;
    right: 0;
    bottom: 10%;
    width: 2px;
    background: linear-gradient(to bottom, transparent, rgba(204, 255, 51, 0.3) 40%, rgba(51, 153, 153, 0.3) 60%, transparent);
}

.summary-brand-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 0 56px;
    z-index: 1;
}

.summary-brand-logo {
    width: 140px;
    height: auto;
    margin-bottom: 28px;
}

.summary-brand-title {
    font-size: 34px;
    font-weight: 800;
    color: #F2F5FA;
    line-height: 1.15;
    letter-spacing: -0.03em;
    margin-bottom: 12px;
}

.summary-brand-sub {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: linear-gradient(135deg, #CCFF33 0%, #66CCCC 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.summary-content {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.summary-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    padding-top: calc(12px + var(--safe-area-inset-top, 0px));
    border-bottom: 1px solid var(--v-outline-base);
}

.summary-origin {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.summary-close {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtSecondary-base);
    transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.summary-close:hover {
    border-color: var(--v-txtSecondary-base);
    color: var(--v-txtPrimary-base);
}

.summary-content-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 20px;
    padding-bottom: calc(24px + var(--safe-area-inset-bottom, 0px));
}

.summary-screen--mobile {
    flex-direction: column;
}

.summary-screen--mobile .summary-brand {
    flex: 0 0 auto;
    min-height: 160px;
}

.summary-screen--mobile .summary-brand-content {
    position: relative;
    padding: calc(28px + var(--safe-area-inset-top, 0px)) 28px 28px;
}

.summary-screen--mobile .summary-brand-accent {
    display: none;
}

.summary-screen--mobile .summary-brand-logo {
    width: 110px;
    margin-bottom: 16px;
}

.summary-screen--mobile .summary-brand-title {
    font-size: 26px;
}

.summary-screen--mobile .summary-content {
    height: auto;
    flex: 1;
}

.summary-screen--mobile .summary-header {
    padding-top: 12px;
}
</style>